import { create } from 'zustand';
import { StepToScreen } from './types';
import get from 'lodash.get';
import update from 'lodash/update';
import { AdditionalSchema, BorrowerSchema, DeclarationSchema, DemographicInfoSchema, NBSSchema, SubjectPropertySchema, TitleInfoSchema } from '../utils';
import { assign, cloneDeep } from 'lodash';
import Functions from '../utils/Functions';
import { produce } from 'immer';
import { ApiLogger } from '../../utils';
import { deNormalization } from '../components/pages/CRTForm/helpers';
import uuid from 'react-native-uuid';
const initialState = {
  isError: false,
  isSuccess: false,
  isLoading: false,
  metadata: undefined,
  data: undefined,
  template: {
    borrowers: {
      steps: [{
        state: 'InProgress',
        step: 1,
        text: 'Borrower'
      }, {
        state: 'InActive',
        step: 2,
        text: 'Additional'
      }, {
        state: 'InActive',
        step: 3,
        text: 'Property'
      }, {
        state: 'InActive',
        step: 4,
        text: 'Loan\nDeclarations'
      }, {
        state: 'InActive',
        step: 5,
        text: 'Demographic'
      }],
      title: 'Review your information',
      subtitle: 'All fields are required. Double check your information while completing this section.',
      disclaimer: `By clicking “Next”, you consent to be contacted, including via automated technology or artificial/prerecorded voice, by Freedom Mortgage and any of its current or future affiliated companies (collectively “Freedom Mortgage”) at the phone number(s) and email address provided for marketing purposes. You affirm that you are the subscriber of the phone number(s) and email address or that the subscriber has authorized you to provide this consent. Your consent is not a condition of purchase of goods or services from Freedom Mortgage. You can revoke your consent at any time by contacting Freedom Mortgage at 855-690-5900.`,
      flags: ['CONFIRM INFO']
    },
    additional: {
      steps: [{
        state: 'Completed',
        step: 1,
        text: 'Borrower'
      }, {
        state: 'InProgress',
        step: 2,
        text: 'Additional'
      }, {
        state: 'InActive',
        step: 3,
        text: 'Property'
      }, {
        state: 'InActive',
        step: 4,
        text: 'Loan\nDeclarations'
      }, {
        state: 'InActive',
        step: 5,
        text: 'Demographic'
      }],
      title: 'Additional personal information',
      subtitle: 'All fields are required. Double check your information while completing this section.',
      disclaimer: '',
      flags: ['CONFIRM INFO']
    },
    property: {
      steps: [{
        state: 'Completed',
        step: 1,
        text: 'Borrower'
      }, {
        state: 'Completed',
        step: 2,
        text: 'Additional'
      }, {
        state: 'InProgress',
        step: 3,
        text: 'Property'
      }, {
        state: 'InActive',
        step: 4,
        text: 'Loan\nDeclarations'
      }, {
        state: 'InActive',
        step: 5,
        text: 'Demographic'
      }],
      title: 'Update property information',
      subtitle: 'All fields are required. Double check your information while completing this section.',
      disclaimer: '',
      flags: ['CONFIRM INFO']
    },
    declarations: {
      steps: [{
        state: 'Completed',
        step: 1,
        text: 'Borrower'
      }, {
        state: 'Completed',
        step: 2,
        text: 'Additional'
      }, {
        state: 'Completed',
        step: 3,
        text: 'Property'
      }, {
        state: 'InProgress',
        step: 4,
        text: 'Loan\nDeclarations'
      }, {
        state: 'InActive',
        step: 5,
        text: 'Demographic'
      }],
      title: 'Complete declarations',
      subtitle: 'All fields are required. Double check your information while completing this section.',
      disclaimer: ``,
      flags: ['INFORMATION REQUIRED']
    },
    demographic: {
      steps: [{
        state: 'Completed',
        step: 1,
        text: 'Borrower'
      }, {
        state: 'Completed',
        step: 2,
        text: 'Additional'
      }, {
        state: 'Completed',
        step: 3,
        text: 'Property'
      }, {
        state: 'Completed',
        step: 4,
        text: 'Loan\nDeclarations'
      }, {
        state: 'InProgress',
        step: 5,
        text: 'Demographic'
      }],
      title: 'Complete demographic information',
      subtitle: 'Double check your information while completing this section.',
      disclaimer: `By clicking the Submit button below, I authorize Freedom Mortgage to run a credit check with one or more consumer agencies to assess my eligibility for a mortgage loan.`,
      flags: ['INFORMATION REQUIRED']
    },
    current: 1,
    previous: 0,
    isEditing: false,
    isRemoving: false,
    modifyingId: undefined,
    screen: 'borrowers'
  }
};
export const useFullAppStore = create((setState, getState) => ({
  ...initialState,
  actions: {
    setNextScreen: async logApiEvent => {
      const {
        url,
        apiKey,
        accessToken,
        applicationId
      } = get(getState(), ['metadata'], {
        url: '',
        apiKey: '',
        accessToken: '',
        applicationId: ''
      });
      const currentScreen = get(getState(), ['template', 'current'], 1);
      setState(produce(getState(), state => {
        if (StepToScreen[state.template.current + 1]) {
          state.template.previous = state.template.current;
          state.template.screen = StepToScreen[state.template.current + 1] || 'borrowers';
          state.template.current = state.template.current + 1;
        }
      }));
      if (currentScreen == 1) {
        ApiLogger(logApiEvent, {
          method: 'post',
          url: url,
          ...deNormalization(getState(), false),
          headers: {
            'x-correlation-id': uuid.v4().toString(),
            'Authorization': `Bearer ${accessToken}`,
            'x-api-key': apiKey
          }
        }).then(response => {
          const applicationId = get(response, ['applicationId'], '');
          setState(produce(getState(), state => {
            if (state.metadata) state.metadata.applicationId = applicationId;
          }));
        }).catch(() => {});
      } else {
        ApiLogger(logApiEvent, {
          method: 'put',
          url: `${url}/${applicationId}`,
          ...deNormalization(getState(), false),
          headers: {
            'x-correlation-id': uuid.v4().toString(),
            'Authorization': `Bearer ${accessToken}`,
            'x-api-key': apiKey
          }
        }).then(() => {}).catch(() => {});
      }
    },
    setPreviousScreen: () => {
      setState(produce(getState(), state => {
        var _state$template;
        if (StepToScreen[(_state$template = state.template) === null || _state$template === void 0 ? void 0 : _state$template.previous]) {
          state.template.screen = StepToScreen[state.template.previous] || 'borrowers';
          state.template.current = state.template.previous;
          state.template.previous -= 1;
        }
      }));
    },
    confirm: function (index) {
      let isCompleted = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      setState(produce(getState(), state => {
        state.template[state.template.screen].flags[Number(index)] = isCompleted ? 'COMPLETED' : 'CONFIRMED';
      }));
    },
    save: _ref => {
      let {
        path,
        value
      } = _ref;
      setState(produce(getState(), state => {
        update(state, path, n => assign(n, state.template.screen === 'demographic' ? cloneDeep(value) : value));
        state.template.modifyingId = undefined;
        state.template.isEditing = false;
      }));
    },
    add: item => {
      setState(produce(getState(), state => {
        state.data.borrowers.push(item);
        state.template[state.template.screen].flags.push('CONFIRM INFO');
      }));
    },
    remove: idx => {
      setState(produce(getState(), state => {
        state.data.borrowers.splice(idx, 1);
        state.template[state.template.screen].flags.splice(Number(idx), 1);
        state.template.isRemoving = false;
        state.template.isEditing = false;
        state.template.modifyingId = undefined;
      }));
    },
    edit: (type, id) => {
      setState(produce(getState(), state => {
        state.template.isEditing = type === 'edit';
        state.template.isRemoving = type === 'remove';
        state.template.modifyingId = type === 'cancel' ? undefined : id;
      }));
    },
    submit: logApiEvent => {
      const {
        url,
        apiKey,
        accessToken,
        applicationId
      } = get(getState(), ['metadata'], {
        url: '',
        apiKey: '',
        accessToken: '',
        applicationId: ''
      });
      setState(produce(getState(), state => {
        state.isLoading = true;
      }));
      ApiLogger(logApiEvent, {
        method: 'put',
        url: `${url}/${applicationId}`,
        ...deNormalization(getState(), true),
        headers: {
          'x-correlation-id': uuid.v4().toString(),
          'Authorization': `Bearer ${accessToken}`,
          'x-api-key': apiKey
        }
      }).then(() => {
        setState(produce(getState(), state => {
          state.isSuccess = true;
          state.isLoading = false;
        }));
      }).catch(() => {
        setState(produce(getState(), state => {
          state.isError = true;
          state.isLoading = false;
        }));
      });
    },
    resetSuccessFlag: () => {
      setState(produce(getState(), state => {
        state.isSuccess = false;
        state.isError = false;
      }));
    }
  }
}));
export const useFullAppActions = () => useFullAppStore(state => state.actions);
export const useSelectTemplate = () => useFullAppStore(state => state.template);
export const useTemplateInfoByScreen = () => useFullAppStore(state => get(state, ['template', `${state.template.screen}`], state.template.borrowers));
export const useSelectTemplatePrevious = () => useSelectTemplate().previous;
export const useSelectBorrowersData = () => useFullAppStore(state => {
  const borrowers = get(state, ['data', 'borrowers'], state.data.borrowers);
  const {
    template: {
      screen
    }
  } = state;
  const valid = borrowers.map((borrower, idx) => {
    try {
      if (screen === 'borrowers') {
        BorrowerSchema.validateSync(borrower);
      } else if (screen === 'additional') AdditionalSchema.validateSync(borrower);else if (screen === 'declarations') DeclarationSchema.validateSync(borrower.declarations);else if (screen === 'demographic') DemographicInfoSchema.validateSync(borrower.demographic);
      return {
        idx,
        valid: true
      };
    } catch (e) {
      return {
        idx,
        valid: false
      };
    }
  }).filter(_ref2 => {
    let {
      valid
    } = _ref2;
    return !valid;
  });
  return {
    borrowers,
    idx: Array.isArray(valid) && valid.length > 0 ? valid[0].idx : undefined
  };
});
export const useSelectScreen = () => useFullAppStore(state => state.template.screen);
export const useNextDisabled = () => useFullAppStore(state => {
  const {
    flags
  } = get(state, ['template', `${[state.template.screen]}`], {
    flags: []
  });
  return flags.some(flag => flag === 'CONFIRM INFO' || flag === 'INFORMATION REQUIRED') || state.template.isEditing || state.template.isRemoving;
});
export const useIsInEditMode = () => useFullAppStore(state => ({
  isEditing: state.template.isEditing,
  isRemoving: state.template.isRemoving,
  modifyingId: state.template.modifyingId
}));
export const useSelectPropertyData = () => useFullAppStore(state => {
  const property = get(state, ['data', 'property'], state.data.property);
  const borrowers = get(state, ['data', 'borrowers'], state.data.borrowers);
  const valid = property.map((property, idx) => {
    try {
      switch (idx) {
        case 0:
          SubjectPropertySchema.validateSync(property);
          break;
        case 1:
          TitleInfoSchema.validateSync(property);
          break;
        case 2:
          Functions.isNBSsectionRequired(borrowers) ? NBSSchema.validateSync(property) : null;
          break;
      }
      return {
        idx,
        valid: true
      };
    } catch (e) {
      return {
        idx,
        valid: false
      };
    }
  }).filter(_ref3 => {
    let {
      valid
    } = _ref3;
    return !valid;
  });
  return {
    property,
    idx: Array.isArray(valid) && valid.length > 0 ? valid[0].idx : undefined
  };
});
export const useSelectBorrowerOptions = function () {
  let id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  const {
    borrowers
  } = useSelectBorrowersData();
  const primary = get(borrowers, [id], undefined);
  const exceptId = borrowers.filter((_, i) => i !== id).map(_ref4 => {
    let {
      name
    } = _ref4;
    return {
      label: name,
      value: name,
      id: name
    };
  });
  return {
    borrowers: [{
      label: primary ? `${primary.firstName} ${primary.lastName}` : '',
      value: primary ? `${primary.firstName} ${primary.lastName}` : '',
      id: primary ? `${primary.firstName} ${primary.lastName}` : '_'
    }],
    otherBorrowers: exceptId
  };
};
//# sourceMappingURL=useFullApp.js.map